import React, {useEffect} from "react";
import { FaLightbulb, FaRocket, FaMagic, FaCode, FaPalette, FaPencilRuler,
    FaEye, FaAward, FaCalendarAlt, FaUsers, FaDollarSign
 } from 'react-icons/fa';
import { motion } from "framer-motion";
import image1 from '../assets/whoweare1.jpg';
import image2 from '../assets/whoweare2.jpg';
import image3 from '../assets/whoweare3.jpg';
import image4 from '../assets/whoweare4.jpg';
import image5 from '../assets/whoweare5.jpg';
import favicon from "../assets/favicon.ico";
import { Helmet } from 'react-helmet';
import uiux from "../assets/ui_ux_home1.jpg";
import software_home from "../assets/software_home.jpg";
import marketing from "../assets/marketing_home.jpg";
import SchemaMarkupComponent from "../components/SchemaMarkupComponent.jsx"

const WhoWeAre = () => {
    const metaUrl = "https://www.techecosolution.in/whoweare";

    const services = [
    { 
      title: "UI/UX Design", 
      image: uiux,
      description: "Professional UI/UX design services for modern web applications"
    },
    { 
      title: "Web Development", 
      image: software_home,
      description: "Full-stack web development solutions using latest technologies"
    },
    { 
      title: "Digital Marketing", 
      image: marketing,
      description: "Complete digital marketing and SEO services to grow your business"
    },
    ];
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Techecosolution (TES)",
        "alternateName": "TES",
        "url": metaUrl,
        "logo": `${metaUrl}${favicon}`,
        "description": "Leading provider of web development, software development, and digital marketing services in India",
        "foundingDate": "2020",
        "founder": {
            "@type": "Person",
            "name": "TES Founder"
        },
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Bangalore",
            "addressRegion": "Karnataka",
            "addressCountry": "IN"
        },
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-9483085077",
            "contactType": "customer service",
            "availableLanguage": ["English", "Hindi"]
        },
        "sameAs": [
            "https://www.facebook.com/techecosolution",
            "https://www.linkedin.com/company/techecosolution",
            "https://twitter.com/techecosolution"
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "150"
        }
      };
    
      const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Techecosolution",
        "url": metaUrl,
        "description": "Professional web development, software development, and digital marketing services",
        "publisher": {
          "@type": "Organization",
          "name": "Techecosolution"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${metaUrl}search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      };
    
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Web Development & Digital Marketing Services",
        "description": "Comprehensive web development, software development, and digital marketing solutions",
        "provider": {
          "@type": "Organization",
          "name": "Techecosolution"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Web Development Services",
          "itemListElement": services.map((service, index) => ({
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": service.title,
              "description": service.description
            }
          }))
        }
      };
    
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": metaUrl
          }
        ]
      };
    
      useEffect(() => {
        // Enhanced SEO meta tags
        document.title = "TES - Who We Are | Leading Web Development & Digital Marketing Agency in India";
        
        // Meta description
        const metaDescription = document.querySelector("meta[name='description']");
        if (metaDescription) {
          metaDescription.setAttribute(
            "content",
            "Techecosolution (TES) is a leading provider of web development, software development, and digital marketing services in India. We specialize in creating innovative digital solutions that empower businesses to thrive in the digital era."
          );
        }
    
        // Meta keywords
        const metaKeywords = document.querySelector("meta[name='keywords']");
        if (metaKeywords) {
          metaKeywords.setAttribute(
            "content",
            "web development, software development, digital marketing, SEO, IT solutions, India"
          );
        }
    
        // Robots meta tag
        let robotsMeta = document.querySelector("meta[name='robots']");
        if (!robotsMeta) {
          robotsMeta = document.createElement("meta");
          robotsMeta.setAttribute("name", "robots");
          document.head.appendChild(robotsMeta);
        }
        robotsMeta.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    
        // Author meta tag
        let authorMeta = document.querySelector("meta[name='author']");
        if (!authorMeta) {
          authorMeta = document.createElement("meta");
          authorMeta.setAttribute("name", "author");
          document.head.appendChild(authorMeta);
        }
        authorMeta.setAttribute("content", "Techecosolution Team");
    
        // Canonical link
        const existingCanonical = document.querySelector("link[rel='canonical']");
        if (existingCanonical) {
          existingCanonical.setAttribute("href", metaUrl);
        } else {
          const canonicalLink = document.createElement("link");
          canonicalLink.setAttribute("rel", "canonical");
          canonicalLink.setAttribute("href", metaUrl);
          document.head.appendChild(canonicalLink);
        }
    
        // Hreflang attributes for different languages/regions
        const hreflangs = [
          { lang: "en", region: "in", url: metaUrl },
          { lang: "en", region: "us", url: metaUrl },
          { lang: "en", region: "gb", url: metaUrl },
          { lang: "x-default", region: "", url: metaUrl }
        ];
    
        // Remove existing hreflang links
        const existingHreflangs = document.querySelectorAll("link[hreflang]");
        existingHreflangs.forEach(link => link.remove());
    
        // Add new hreflang links
        hreflangs.forEach(({ lang, region, url }) => {
          const hreflangLink = document.createElement("link");
          hreflangLink.setAttribute("rel", "alternate");
          hreflangLink.setAttribute("hreflang", region ? `${lang}-${region}` : lang);
          hreflangLink.setAttribute("href", url);
          document.head.appendChild(hreflangLink);
        });
    
        // Favicon
        const existingFavicon = document.querySelector("link[rel='icon']");
        if (existingFavicon) {
          existingFavicon.setAttribute("href", favicon);
        } else {
          const faviconLink = document.createElement("link");
          faviconLink.setAttribute("rel", "icon");
          faviconLink.setAttribute("type", "image/x-icon");
          faviconLink.setAttribute("href", favicon);
          document.head.appendChild(faviconLink);
        }
    
        // Apple touch icon
        let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
        if (!appleTouchIcon) {
          appleTouchIcon = document.createElement("link");
          appleTouchIcon.setAttribute("rel", "apple-touch-icon");
          appleTouchIcon.setAttribute("sizes", "180x180");
          appleTouchIcon.setAttribute("href", favicon);
          document.head.appendChild(appleTouchIcon);
        }
    
        // Preconnect to external domains
        const preconnectDomains = [
          "https://fonts.googleapis.com",
          "https://fonts.gstatic.com",
          "https://www.google-analytics.com"
        ];
    
        preconnectDomains.forEach(domain => {
          let preconnectLink = document.querySelector(`link[href="${domain}"]`);
          if (!preconnectLink) {
            preconnectLink = document.createElement("link");
            preconnectLink.setAttribute("rel", "preconnect");
            preconnectLink.setAttribute("href", domain);
            document.head.appendChild(preconnectLink);
          }
        });
    
        // DNS prefetch for performance
        const dnsPrefetchDomains = [
          "//www.googletagmanager.com",
          "//connect.facebook.net"
        ];
    
        dnsPrefetchDomains.forEach(domain => {
          let dnsPrefetchLink = document.querySelector(`link[href="${domain}"]`);
          if (!dnsPrefetchLink) {
            dnsPrefetchLink = document.createElement("link");
            dnsPrefetchLink.setAttribute("rel", "dns-prefetch");
            dnsPrefetchLink.setAttribute("href", domain);
            document.head.appendChild(dnsPrefetchLink);
          }
        });
    
      }, []);
  return (
    <>
        <SchemaMarkupComponent />
        <Helmet>
            {/* Primary Meta Tags */}
            <title>Techecosolution - Best Web Development & Digital Marketing Services in India</title>
            <meta name="title" content="Techecosolution - Best Web Development & Digital Marketing Services in India" />
            <meta name="description" content="Techecosolution (TES) - India's leading web development, software development & digital marketing agency. Expert UI/UX design, responsive websites, SEO services & digital solutions. Serving Bangalore & across India." />
            <meta name="keywords" content="web development India, software development Bangalore, digital marketing services, SEO services India, UI/UX design, responsive website design, Techecosolution, TES" />
            <meta name="author" content="Techecosolution Team" />
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta name="language" content="English" />
            <meta name="revisit-after" content="7 days" />
            <meta name="distribution" content="web" />
            <meta name="rating" content="general" />
            <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Techecosolution" />
            <meta property="og:url" content={metaUrl} />
            <meta property="og:title" content="TES | Best Web Development & Digital Marketing Services in India" />
            <meta property="og:description" content="Techecosolution (TES) - India's leading web development, software development & digital marketing agency. Expert solutions for your business growth." />
            <meta property="og:image" content={`${metaUrl}${image5}`} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Techecosolution - Web Development & Digital Marketing Services" />
            <meta property="og:locale" content="en_IN" />
            <meta property="og:locale:alternate" content="en_US" />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:site" content="@techecosolution" />
            <meta property="twitter:creator" content="@techecosolution" />
            <meta property="twitter:url" content={metaUrl} />
            <meta property="twitter:title" content="TES | Best Web Development & Digital Marketing Services in India" />
            <meta property="twitter:description" content="Techecosolution (TES) - India's leading web development, software development & digital marketing agency." />
            <meta property="twitter:image" content={`${metaUrl}${image5}`} />
            <meta property="twitter:image:alt" content="Techecosolution Services" />

            {/* Additional SEO Meta Tags */}
            <meta name="theme-color" content="#0e5cae" />
            <meta name="msapplication-TileColor" content="#0e5cae" />
            <meta name="msapplication-config" content="/browserconfig.xml" />
            
            {/* Geo Tags */}
            <meta name="geo.region" content="IN-KA" />
            <meta name="geo.placename" content="Bangalore" />
            <meta name="geo.position" content="12.9716;77.5946" />
            <meta name="ICBM" content="12.9716, 77.5946" />

            {/* Business/Organization Tags */}
            <meta name="business:contact_data:street_address" content="Bangalore, Karnataka" />
            <meta name="business:contact_data:locality" content="Bangalore" />
            <meta name="business:contact_data:region" content="Karnataka" />
            <meta name="business:contact_data:postal_code" content="560001" />
            <meta name="business:contact_data:country_name" content="India" />

            {/* Schema Markup */}
            <script type="application/ld+json">
            {JSON.stringify(organizationSchema)}
            </script>
            <script type="application/ld+json">
            {JSON.stringify(websiteSchema)}
            </script>
            <script type="application/ld+json">
            {JSON.stringify(serviceSchema)}
            </script>
            <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
            </script>

            {/* Hreflang Tags */}
            <link rel="alternate" hreflang="en-in" href={metaUrl} />
            <link rel="alternate" hreflang="en-us" href={metaUrl} />
            <link rel="alternate" hreflang="en-gb" href={metaUrl} />
            <link rel="alternate" hreflang="x-default" href={metaUrl} />

            {/* Canonical URL */}
            <link rel="canonical" href={metaUrl} />

            {/* Preconnect for Performance */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link rel="dns-prefetch" href="//www.google-analytics.com" />
            <link rel="dns-prefetch" href="//www.googletagmanager.com" />
        </Helmet>
        <section className="flex flex-col gap-8 md:gap-12 lg:gap-16 p-16 md:p-8 lg:p-12 bg-gray-100">
            {/* About Section */}
            <div className="flex flex-col lg:flex-row justify-between items-center gap-8 md:gap-12">
                <div className="flex-1 w-full lg:max-w-1/2">
                    <div className="mt-8 md:mt-12 lg:mt-16 mb-4">
                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-blue-700 mb-4">Transforming <span className="text-black">Ideas</span> <div className="text-black">into Digital Reality </div> </h3>
                        <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8">
                            <span className="text-lg md:text-xl text-blue-700 font-bold">Smart IT Solutions for Digital Era : </span> 
                            We are a consulting agency dedicated to making things work better for you. 
                            Our expertise includes delivering comprehensive website solutions tailored for businesses, 
                            from design and development to SEO and maintenance. With a focus on transforming ideas into 
                            powerful digital experiences, we are here to help you achieve extraordinary results. Let’s create something remarkable together!
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 md:gap-6 lg:gap-8 justify-center items-center">
                        {/* Team Members Card */}
                        <div className="bg-gradient-to-r from-[#d5dae3] to-[#c9d1db] shadow-lg rounded-lg p-4 md:p-6 w-32 md:w-36 lg:w-40 text-center transform hover:scale-105 transition-transform duration-300">
                            <h2 className="text-2xl md:text-3xl font-bold text-blue-700 mb-2">17+</h2>
                            <p className="text-sm md:text-base text-gray-800">Team Members</p>
                        </div>

                        {/* Happy Clients Card */}
                        <div className="bg-gradient-to-r from-[#c9d1db] to-[#b8c1d0] shadow-lg rounded-lg p-4 md:p-6 w-32 md:w-36 lg:w-40 text-center transform hover:scale-105 transition-transform duration-300">
                            <h2 className="text-2xl md:text-3xl font-bold text-blue-700 mb-2">3+</h2>
                            <p className="text-sm md:text-base text-gray-800">Happy Clients</p>
                        </div>

                        {/* Client Satisfaction Card */}
                        <div className="bg-gradient-to-r from-[#b8c1d0] to-[#a7b0c3] shadow-lg rounded-lg p-4 md:p-6 w-32 md:w-36 lg:w-40 text-center transform hover:scale-105 transition-transform duration-300">
                            <h2 className="text-2xl md:text-3xl font-bold text-blue-700 mb-2">99.9%</h2>
                            <p className="text-sm md:text-base text-gray-800">Client Satisfaction</p>
                        </div>
                    </div>
                </div>
                <div className="flex-1 flex justify-center w-full">
                    <div className="grid mt-8 md:mt-16 lg:mt-24 mb-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-full">
                        {/* Image 1 - Large Vertical Card */}
                        <div
                            className="w-full sm:w-40 md:w-44 lg:w-48 h-48 md:h-56 lg:h-64 rounded-xl bg-cover bg-center shadow-xl transform hover:scale-105 transition-transform duration-300"
                            style={{
                                backgroundImage: image1 ? `url(${image1})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                            >
                            <div className="w-full h-full rounded-xl bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                                <p className="text-white font-bold">Featured Project</p>
                            </div>
                        </div>
                        <div
                            className="w-full sm:w-48 md:w-56 lg:w-64 h-32 md:h-36 lg:h-40 rounded-lg bg-cover bg-center shadow-lg transform hover:scale-105 transition-transform duration-300"
                            style={{
                                backgroundImage: image2 ? `url(${image2})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                            >
                            <div className="w-full h-full rounded-lg bg-gradient-to-t from-black/50 to-transparent flex items-end p-4">
                                <p className="text-white font-semibold">Team Collaboration</p>
                            </div>
                        </div>
                        <div
                            className="w-full sm:w-36 md:w-40 lg:w-44 h-36 md:h-40 lg:h-44 mt-0 sm:mt-8 md:mt-12 lg:mt-16 rounded-lg bg-cover bg-center shadow-md transform hover:scale-105 hover:rotate-1 transition-transform duration-300"
                            style={{
                                backgroundImage: image4 ? `url(${image4})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                            >
                            <div className="w-full h-full rounded-lg bg-gradient-to-t from-black/40 to-transparent flex items-end p-4">
                            <p className="text-white font-bold text-sm md:text-base lg:text-lg">Innovation & excellence.</p>
                            </div>
                        </div>
                        <div
                            className="w-full h-40 md:h-44 lg:h-48 col-span-1 sm:col-span-2 rounded-lg bg-cover bg-center shadow-xl transform hover:scale-105 transition-transform duration-300"
                            style={{
                                backgroundImage: image3 ? `url(${image3})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                            >
                            <div className="w-full h-full rounded-lg bg-gradient-to-t from-black/60 to-transparent flex items-end p-4 md:p-6">
                                <p className="text-white font-bold text-sm md:text-base lg:text-lg">Innovation Hub</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Vision and Mission Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                {/* Vision Card */}
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 shadow-lg rounded-lg p-6 md:p-8 flex flex-col items-start lg:items-center text-left lg:text-center">
                    <div className="flex items-center gap-3 md:gap-4 mb-4">
                        <FaLightbulb className="text-yellow-400 text-3xl md:text-4xl" />
                        <h2 className="text-2xl md:text-3xl font-bold text-blue-600">Our Vision</h2>
                    </div>
                    <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                        To be the trailblazers in innovation, creating a future where technology seamlessly empowers every aspect of life.
                        We envision a world connected by ideas, driven by passion, and transformed by creativity.
                    </p>
                </div>

                {/* Mission Card */}
                <div className="bg-gradient-to-r from-green-100 to-green-200 shadow-lg rounded-lg p-6 md:p-8 flex flex-col items-start lg:items-center text-left lg:text-center">
                    <div className="flex items-center gap-3 md:gap-4 mb-4">
                        <FaRocket className="text-yellow-400 text-3xl md:text-4xl" />
                        <h2 className="text-2xl md:text-3xl font-bold text-green-600">Our Mission</h2>
                    </div>
                    <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                        To turn bold ideas into transformative digital solutions by fostering innovation, collaboration, and excellence.
                        We aim to empower businesses and individuals to achieve their goals through cutting-edge technology and user-friendly designs.
                    </p>
                </div>
            </div>
            <div className="relative p-6 md:p-8 lg:p-10 rounded-xl shadow-2xl bg-gradient-to-b from-gray-900 via-black to-gray-950 border border-gray-800">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 md:mb-10 lg:mb-12 text-orange-400 text-center">
                    Our Design Process
                </h3>
                <div className="flex flex-wrap justify-center gap-4 md:gap-6 lg:gap-8">
                    {[
                    { icon: <FaEye />, title: "Research", description: "Understanding user needs" },
                    { icon: <FaPencilRuler />, title: "Wireframing", description: "Structure & layout" },
                    { icon: <FaPalette />, title: "Visual Design", description: "Aesthetics & branding" },
                    { icon: <FaCode />, title: "Development", description: "Bringing designs to life" },
                    { icon: <FaMagic />, title: "Testing", description: "Refining the experience" },
                    { icon: <FaRocket />, title: "Deployment", description: "Enhancing the experience" },
                    ].map((step, i) => (
                    <motion.div
                        key={`process-${i}`}
                        className="w-40 md:w-44 lg:w-48 bg-gray-800 rounded-lg shadow-lg p-4 md:p-5 lg:p-6 relative flex flex-col items-center transform hover:scale-105 transition-transform duration-300"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                    >
                        {/* Icon */}
                        <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-r from-lime-500 to-cyan-500 flex items-center justify-center mb-4 shadow-md">
                        <div className="text-2xl md:text-2xl lg:text-3xl text-orange-300">{step.icon}</div>
                        </div>
                        {/* Title */}
                        <h4 className="text-base md:text-lg font-bold text-lime-400 mb-2 text-center">
                        {step.title}
                        </h4>
                        {/* Description */}
                        <p className="text-sm md:text-base text-gray-300 text-center">{step.description}</p>
                    </motion.div>
                    ))}
                </div>
            </div>

            <section className="relative p-6 md:p-8 lg:p-10 rounded-xl shadow-2xl bg-gradient-to-b from-gray-900 via-black to-gray-950 border border-gray-800">
                <div className="content-container mb-12 md:mb-14 lg:mb-16">
                    {/* About Us Section */}
                    <div className="heading text-center mb-8 md:mb-10 lg:mb-12">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold">Smart IT Solutions for Digital Era</h1>
                    <p className="description text-gray-400 mt-3 md:mt-4 text-sm md:text-base">
                        Empowering businesses with cutting-edge IT solutions tailored for the digital age. From strategy to execution, we deliver seamless technology integrations to help you thrive in an ever-evolving digital landscape.
                    </p>
                    </div>
                </div>
                {/* Why Trust Us Section */}
                <div className="trust-us-section grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
                    <div className="relative order-2 lg:order-1">
                    <div className="w-full h-full rounded-xl overflow-hidden shadow-xl">
                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-white mb-4">Why <span className="text-blue-700">Trust Us </span> <div className="text-white">For Your Needs </div> </h3>
                        <img src={image5} alt="TES" className="w-full mt-4 md:mt-12 lg:mt-16 h-auto rounded-lg shadow-md" />
                    </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 order-1 lg:order-2">
                    {[
                        { 
                            icon: <FaDollarSign />, 
                            title: "Affordable Price", 
                            description: "We offer cost-effective IT solutions without compromising quality, ensuring value for every investment." 
                        },
                        { 
                            icon: <FaUsers />, 
                            title: "Professional Team", 
                            description: "Our team of experienced professionals is dedicated to delivering innovative and reliable IT services." 
                        },
                        { 
                            icon: <FaCalendarAlt />, 
                            title: "7+ Years Experience", 
                            description: "With over 7 years of industry expertise, we provide trusted and proven IT solutions tailored to your needs." 
                        },
                        { 
                            icon: <FaAward />, 
                            title: "Award Winning", 
                            description: "Recognized for excellence in IT services, we bring award-winning solutions to drive your success forward." 
                        },
                    ].map((point, i) => (
                        <div
                        key={`trust-point-${i}`}
                        className="p-4 md:p-5 lg:p-6 rounded-lg bg-gray-800 shadow-lg flex flex-col items-center transform hover:scale-105 transition-transform duration-300"
                        >
                        <div className="w-12 h-12 md:w-13 md:h-13 lg:w-14 lg:h-14 rounded-full bg-gradient-to-r from-teal-500 to-purple-500 flex items-center justify-center mb-3 md:mb-4 shadow-md">
                            <div className="text-xl md:text-xl lg:text-2xl text-orange-300">{point.icon}</div>
                        </div>
                        <h4 className="text-base md:text-lg font-bold text-lime-400 mb-2 text-center">
                            {point.title}
                        </h4>
                        <p className="text-sm md:text-base text-gray-300 text-center">{point.description}</p>
                        </div>
                    ))}
                    </div>
                </div>
            </section>
        </section>
    </>
  );
};

export default WhoWeAre;